import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Bookmark, Check } from 'lucide-react';
import { apiGet } from '../lib/api';
import Reveal from '../components/Reveal';

export default function FindParts() {
  const nav = useNavigate();
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brand, setBrand] = useState(null);
  const [model, setModel] = useState(null);
  const [cat, setCat] = useState(null);
  const [garage, setGarage] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('tpu_garage')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    apiGet('/brands').then(setBrands).catch(() => {});
    apiGet('/categories').then(setCategories).catch(() => {});
  }, []);

  const selBrand = brands.find((b) => b.id === brand);
  const matches = selBrand && cat ? categories.find((c) => c.id === cat)?.product_count ?? 0 : 0;
  const ready = selBrand && model && cat;

  const save = () => {
    const next = [{ brand: selBrand.name, model }, ...garage.filter((g) => !(g.brand === selBrand.name && g.model === model))].slice(0, 4);
    setGarage(next);
    localStorage.setItem('tpu_garage', JSON.stringify(next));
  };

  const Step = ({ n, title, children, done }) => (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <span className={`grid place-items-center w-9 h-9 rounded-full font-display font-bold text-lg ${done ? 'bg-flame text-white' : 'bg-forest text-cream'}`}>
          {done ? <Check className="w-4 h-4" /> : n}
        </span>
        <h2 className="font-display font-bold uppercase tracking-tight text-forest text-2xl">{title}</h2>
      </div>
      {children}
    </div>
  );

  const chip = (active) =>
    `font-display font-semibold uppercase tracking-wide px-5 py-2.5 rounded-full border-2 transition ${
      active ? 'bg-flame border-flame text-white' : 'border-emerald-950/15 text-forest hover:border-flame'
    }`;

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-forest noise relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">The parts finder</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-none">
            Find parts for<br />my tractor.
          </h1>
          <p className="text-cream/50 text-sm mt-3 max-w-md">Three quick steps and we'll show every part that matches your machine.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-[1fr_340px] gap-10 items-start">
        <div>
          <Step n={1} title="Select your tractor brand" done={!!selBrand}>
            <div className="flex flex-wrap gap-2">
              {brands.map((b) => (
                <button
                  key={b.id}
                  data-testid={`find-parts-brand-${b.slug}`}
                  onClick={() => { setBrand(b.id); setModel(null); }}
                  className={chip(brand === b.id)}
                >
                  {b.name}
                </button>
              ))}
            </div>
          </Step>

          <AnimatePresence>
            {selBrand && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <Step n={2} title="Select your model" done={!!model}>
                  <div className="flex flex-wrap gap-2">
                    {selBrand.models.map((m) => (
                      <button key={m} data-testid={`find-parts-model-${m.replace(/\s+/g, '-')}`} onClick={() => setModel(m)} className={chip(model === m)}>
                        {m}
                      </button>
                    ))}
                  </div>
                </Step>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {model && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <Step n={3} title="What do you need?" done={!!cat}>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((c) => (
                      <button key={c.id} data-testid={`find-parts-category-${c.slug}`} onClick={() => setCat(c.id)} className={chip(cat === c.id)}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                </Step>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <aside className="lg:sticky lg:top-24 space-y-5">
          <div className="bg-forest text-cream rounded-3xl p-7 noise relative overflow-hidden">
            <p className="font-mono text-[10px] uppercase tracking-widest2 text-flame mb-2">Matching parts</p>
            <motion.p
              key={matches}
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 14 }}
              data-testid="find-parts-match-count"
              className="font-display font-extrabold text-7xl leading-none"
            >
              {ready ? matches : selBrand ? '—' : '···'}
            </motion.p>
            <p className="text-cream/50 text-sm mt-2">
              {ready
                ? `${selBrand.name} ${model} · ${categories.find((c) => c.id === cat)?.name}`
                : 'Complete all three steps to see your matches.'}
            </p>
            {ready && (
              <Link
                to={`/shop?brand=${encodeURIComponent(selBrand.name)}&category=${categories.find((c) => c.id === cat).slug}`}
                data-testid="find-parts-show-results-button"
                className="mt-6 w-full inline-flex justify-center items-center gap-2 bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-6 py-3.5 rounded-full transition-colors"
              >
                Show Matching Parts <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {ready && (
              <button onClick={save} data-testid="save-to-garage-button" className="mt-3 w-full inline-flex justify-center items-center gap-2 border-2 border-white/20 hover:border-flame text-cream hover:text-flame font-display font-semibold uppercase tracking-wide px-6 py-3 rounded-full transition-colors">
                <Bookmark className="w-4 h-4" /> Save to My Garage
              </button>
            )}
          </div>

          {garage.length > 0 && (
            <div className="bg-white border border-emerald-950/10 rounded-3xl p-6">
              <h3 className="font-display font-bold uppercase text-forest mb-3">My Garage</h3>
              <div className="space-y-2">
                {garage.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`garage-entry-${i}`}
                    onClick={() => nav(g)}
                    className="w-full flex items-center justify-between text-sm px-4 py-2.5 rounded-xl border border-emerald-950/10 hover:border-flame transition"
                  >
                    <span className="font-semibold text-forest">{g.brand} <span className="text-sub font-normal">{g.model}</span></span>
                    <ArrowRight className="w-4 h-4 text-flame" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
